/**
 * DriverRouteExecutionPage - Execucao de rota para motorista
 *
 * Funcionalidades:
 * - Navegacao turn-by-turn
 * - Lista de paradas com acoes
 * - Prova de entrega simplificada
 * - Integracao com apps de navegacao
 */

import { useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Collapse,
  Alert,
  CircularProgress,
  Fab,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Navigation as NavigateIcon,
  CheckCircle as CompleteIcon,
  SkipNext as SkipIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Phone as PhoneIcon,
  Place as PlaceIcon,
  AccessTime as TimeIcon,
  Edit as NotesIcon,
  Done as DoneIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import { useEffectAsync } from '../reactHelper';
import routePlanService from '../common/services/routePlanService';
import routeExecutionService from '../routing/services/routeExecutionService';

// Componente de parada
const StopItem = ({
 stop, index, isNext, isCompleted, onComplete, onSkip, onNavigate, onCall, tokens 
}) => {
  const [expanded, setExpanded] = useState(isNext);

  return (
    <Card
      sx={{
        backgroundColor: isNext ? `${tokens?.accent?.primary}08` : tokens?.surface?.raised,
        borderRadius: tokens?.radius?.lg,
        border: `1px solid ${isNext ? tokens?.accent?.primary : tokens?.border?.subtle}`,
        mb: 1.5,
        opacity: isCompleted ? 0.6 : 1,
      }}
    >
      <ListItem
        button
        onClick={() => setExpanded(!expanded)}
        sx={{ py: 1.5 }}
      >
        <ListItemIcon sx={{ minWidth: 40 }}>
          {isCompleted ? (
            <CompleteIcon sx={{ color: tokens?.semantic?.success }} />
          ) : (
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                backgroundColor: isNext ? tokens?.accent?.primary : tokens?.surface?.elevated,
                color: isNext ? '#fff' : tokens?.text?.secondary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              {index + 1}
            </Box>
          )}
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography
              sx={{
                color: tokens?.text?.primary,
                fontWeight: isNext ? 600 : 400,
                fontSize: '15px',
                textDecoration: isCompleted ? 'line-through' : 'none',
              }}
            >
              {stop.name}
            </Typography>
          }
          secondary={
            <Typography
              sx={{
                color: tokens?.text?.tertiary,
                fontSize: '13px',
              }}
              noWrap
            >
              {stop.address}
            </Typography>
          }
        />
        <ListItemSecondaryAction>
          {isNext && (
            <Chip
              label="Proximo"
              size="small"
              sx={{
                backgroundColor: tokens?.accent?.primary,
                color: '#fff',
                fontWeight: 600,
                fontSize: '10px',
                height: 22,
                mr: 1,
              }}
            />
          )}
          {expanded ? <CollapseIcon /> : <ExpandIcon />}
        </ListItemSecondaryAction>
      </ListItem>

      <Collapse in={expanded}>
        <Divider sx={{ borderColor: tokens?.border?.subtle }} />
        <CardContent sx={{ pt: 1.5, pb: 2 }}>
          {/* Endereco completo */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
            <PlaceIcon sx={{ fontSize: 18, color: tokens?.text?.tertiary, mt: 0.3 }} />
            <Typography sx={{ color: tokens?.text?.secondary, fontSize: '14px' }}>
              {stop.address}
            </Typography>
          </Box>

          {/* Horario estimado */}
          {stop.estimatedArrival && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <TimeIcon sx={{ fontSize: 18, color: tokens?.text?.tertiary }} />
              <Typography sx={{ color: tokens?.text?.secondary, fontSize: '14px' }}>
                Chegada prevista: {new Date(stop.estimatedArrival).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
              </Typography>
            </Box>
          )}

          {/* Notas */}
          {stop.notes && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
              <NotesIcon sx={{ fontSize: 18, color: tokens?.text?.tertiary, mt: 0.3 }} />
              <Typography sx={{ color: tokens?.text?.secondary, fontSize: '14px' }}>
                {stop.notes}
              </Typography>
            </Box>
          )}

          {/* Acoes */}
          {!isCompleted && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<NavigateIcon />}
                onClick={() => onNavigate(stop)}
                sx={{
                  backgroundColor: tokens?.accent?.primary,
                  '&:hover': { backgroundColor: tokens?.accent?.hover },
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Navegar
              </Button>
              {stop.phone && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PhoneIcon />}
                  onClick={() => onCall(stop.phone)}
                  sx={{
                    borderColor: tokens?.border?.default,
                    color: tokens?.text?.secondary,
                    textTransform: 'none',
                  }}
                >
                  Ligar
                </Button>
              )}
              <Button
                variant="contained"
                size="small"
                startIcon={<DoneIcon />}
                onClick={() => onComplete(stop)}
                sx={{
                  backgroundColor: tokens?.semantic?.success,
                  '&:hover': { backgroundColor: '#16a34a' },
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Concluir
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SkipIcon />}
                onClick={() => onSkip(stop)}
                sx={{
                  borderColor: tokens?.semantic?.warning,
                  color: tokens?.semantic?.warning,
                  textTransform: 'none',
                }}
              >
                Pular
              </Button>
            </Box>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};

const DriverRouteExecutionPage = () => {
  const tokens = usePremiumTokens();
  const navigate = useNavigate();
  const { routeId } = useParams();

  // Estado
  const [plan, setPlan] = useState(null);
  const [stops, setStops] = useState([]);
  const [completedStops, setCompletedStops] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dialogs
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [currentStop, setCurrentStop] = useState(null);
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const [skipReason, setSkipReason] = useState('');

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Carregar dados
  useEffectAsync(async () => {
    if (!routeId) return;

    setIsLoading(true);
    setError(null);

    try {
      const summary = await routePlanService.loadCompletePlan(routeId);
      setPlan(summary.plan);

      // Ordenar paradas
      const orderedStops = (summary.stops || []).sort((a, b) => a.optimizedOrder - b.optimizedOrder);
      setStops(orderedStops);

      // Paradas concluidas
      const completed = orderedStops
        .filter((s) => s.status === 'completed' || s.status === 'skipped')
        .map((s) => s.id);
      setCompletedStops(completed);
    } catch (err) {
      console.error('[DriverRouteExecutionPage] Error:', err);
      setError('Erro ao carregar rota');
    } finally {
      setIsLoading(false);
    }
  }, [routeId]);

  // Proxima parada
  const nextStop = useMemo(() => {
    return stops.find((s) => !completedStops.includes(s.id));
  }, [stops, completedStops]);

  // Progresso
  const progress = useMemo(() => {
    if (stops.length === 0) return 0;
    return (completedStops.length / stops.length) * 100;
  }, [stops, completedStops]);

  // Handlers
  const handleNavigate = useCallback((stop) => {
    const lat = stop.latitude || stop.lat;
    const lng = stop.longitude || stop.lng;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
    window.open(url, '_blank');
  }, []);

  const handleCall = useCallback((phone) => {
    window.location.href = `tel:${phone}`;
  }, []);

  const handleOpenComplete = useCallback((stop) => {
    setCurrentStop(stop);
    setDeliveryNotes('');
    setCompleteDialogOpen(true);
  }, []);

  const handleCompleteStop = useCallback(async () => {
    if (!currentStop) return;

    setIsSaving(true);

    try {
      await routeExecutionService.completeStop(routeId, currentStop.id, {
        notes: deliveryNotes,
      });

      setCompletedStops((prev) => [...prev, currentStop.id]);
      setCompleteDialogOpen(false);
      setSnackbar({ open: true, message: 'Entrega concluida!', severity: 'success' });
    } catch (err) {
      console.error('[DriverRouteExecutionPage] Complete error:', err);
      setSnackbar({ open: true, message: 'Erro ao concluir entrega', severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [currentStop, deliveryNotes, routeId]);

  const handleOpenSkip = useCallback((stop) => {
    setCurrentStop(stop);
    setSkipReason('');
    setSkipDialogOpen(true);
  }, []);

  const handleSkipStop = useCallback(async () => {
    if (!currentStop) return;

    setIsSaving(true);

    try {
      await routeExecutionService.skipStop(routeId, currentStop.id, skipReason);

      setCompletedStops((prev) => [...prev, currentStop.id]);
      setSkipDialogOpen(false);
      setSnackbar({ open: true, message: 'Parada pulada', severity: 'warning' });
    } catch (err) {
      console.error('[DriverRouteExecutionPage] Skip error:', err);
      setSnackbar({ open: true, message: 'Erro ao pular parada', severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [currentStop, skipReason, routeId]);

  // Loading
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Erro
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={() => navigate(-1)}>Voltar</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          backgroundColor: tokens?.surface?.raised,
          borderBottom: `1px solid ${tokens?.border?.subtle}`,
          px: 2,
          py: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <IconButton size="small" onClick={() => navigate(-1)}>
            <BackIcon sx={{ color: tokens?.text?.secondary }} />
          </IconButton>
          <Typography
            sx={{
              color: tokens?.text?.primary,
              fontWeight: 600,
              fontSize: '18px',
              flex: 1,
            }}
          >
            {plan?.name || 'Execucao de Rota'}
          </Typography>
        </Box>

        {/* Progresso */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              flex: 1,
              height: 8,
              borderRadius: 4,
              backgroundColor: tokens?.surface?.elevated,
              '& .MuiLinearProgress-bar': {
                backgroundColor: tokens?.accent?.primary,
                borderRadius: 4,
              },
            }}
          />
          <Typography sx={{ color: tokens?.text?.secondary, fontSize: '14px', fontWeight: 600 }}>
            {completedStops.length}/{stops.length}
          </Typography>
        </Box>
      </Box>

      {/* Proxima parada destacada */}
      {nextStop && (
        <Box
          sx={{
            backgroundColor: `${tokens?.accent?.primary}10`,
            px: 2,
            py: 2,
            borderBottom: `1px solid ${tokens?.border?.subtle}`,
          }}
        >
          <Typography
            sx={{
              color: tokens?.text?.tertiary,
              fontSize: '11px',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              mb: 0.5,
            }}
          >
            Proxima Parada
          </Typography>
          <Typography
            sx={{
              color: tokens?.text?.primary,
              fontWeight: 700,
              fontSize: '18px',
              mb: 0.5,
            }}
          >
            {nextStop.name}
          </Typography>
          <Typography
            sx={{
              color: tokens?.text?.secondary,
              fontSize: '14px',
              mb: 2,
            }}
          >
            {nextStop.address}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<NavigateIcon />}
              onClick={() => handleNavigate(nextStop)}
              sx={{
                backgroundColor: tokens?.accent?.primary,
                '&:hover': { backgroundColor: tokens?.accent?.hover },
                textTransform: 'none',
                fontWeight: 600,
                py: 1.5,
              }}
            >
              Iniciar Navegacao
            </Button>
          </Box>
        </Box>
      )}

      {/* Lista de paradas */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {stops.map((stop, index) => (
          <StopItem
            key={stop.id}
            stop={stop}
            index={index}
            isNext={nextStop?.id === stop.id}
            isCompleted={completedStops.includes(stop.id)}
            onComplete={handleOpenComplete}
            onSkip={handleOpenSkip}
            onNavigate={handleNavigate}
            onCall={handleCall}
            tokens={tokens}
          />
        ))}

        {/* Rota concluida */}
        {completedStops.length === stops.length && stops.length > 0 && (
          <Card
            sx={{
              backgroundColor: `${tokens?.semantic?.success}15`,
              borderRadius: tokens?.radius?.lg,
              border: `1px solid ${tokens?.semantic?.success}`,
              textAlign: 'center',
              py: 4,
            }}
          >
            <CompleteIcon sx={{ fontSize: 64, color: tokens?.semantic?.success, mb: 2 }} />
            <Typography
              sx={{
                color: tokens?.text?.primary,
                fontWeight: 700,
                fontSize: '20px',
                mb: 1,
              }}
            >
              Rota Concluida!
            </Typography>
            <Typography
              sx={{
                color: tokens?.text?.secondary,
                fontSize: '14px',
                mb: 3,
              }}
            >
              Todas as paradas foram finalizadas
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/driver')}
              sx={{
                backgroundColor: tokens?.semantic?.success,
                '&:hover': { backgroundColor: '#16a34a' },
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Voltar ao Inicio
            </Button>
          </Card>
        )}
      </Box>

      {/* FAB de navegacao rapida */}
      {nextStop && (
        <Fab
          color="primary"
          onClick={() => handleNavigate(nextStop)}
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 16,
            backgroundColor: tokens?.accent?.primary,
            '&:hover': { backgroundColor: tokens?.accent?.hover },
          }}
        >
          <NavigateIcon />
        </Fab>
      )}

      {/* Dialog de conclusao */}
      <Dialog
        open={completeDialogOpen}
        onClose={() => setCompleteDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: tokens?.surface?.raised,
            borderRadius: tokens?.radius?.lg,
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontWeight: 600 }}>Confirmar Entrega</Typography>
          <IconButton size="small" onClick={() => setCompleteDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: tokens?.text?.secondary, mb: 2 }}>
            {currentStop?.name}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Observacoes (opcional)"
            value={deliveryNotes}
            onChange={(e) => setDeliveryNotes(e.target.value)}
            placeholder="Ex: Entregue na portaria"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setCompleteDialogOpen(false)}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleCompleteStop}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={16} /> : <DoneIcon />}
            sx={{
              backgroundColor: tokens?.semantic?.success,
              '&:hover': { backgroundColor: '#16a34a' },
            }}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de pular */}
      <Dialog
        open={skipDialogOpen}
        onClose={() => setSkipDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: tokens?.surface?.raised,
            borderRadius: tokens?.radius?.lg,
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontWeight: 600 }}>Pular Parada</Typography>
          <IconButton size="small" onClick={() => setSkipDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: tokens?.text?.secondary, mb: 2 }}>
            {currentStop?.name}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Motivo"
            value={skipReason}
            onChange={(e) => setSkipReason(e.target.value)}
            placeholder="Ex: Cliente ausente"
            required
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setSkipDialogOpen(false)}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSkipStop}
            disabled={isSaving || !skipReason.trim()}
            startIcon={isSaving ? <CircularProgress size={16} /> : <SkipIcon />}
            sx={{
              backgroundColor: tokens?.semantic?.warning,
              '&:hover': { backgroundColor: '#d97706' },
            }}
          >
            Pular
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
};

export default DriverRouteExecutionPage;
